import dayjs from 'dayjs';

import { AuthState } from '@/context/AuthContext';

import { TypeLink_internalFields } from '@/core/generated-types/TypeLink_internal';

import { PaymentOptionEnum } from '@/hooks/subFlowAnalytics';

import { noticeError } from '@/utils/newrelicHelpers';

declare global {
  interface Window {
    rudderanalytics: Record<string, any>;
  }
}

interface IdentifyUserTraits {
  email?: string;
  firstName?: string;
  childBirthDate?: Date;
}

interface TrackCodeAppliedParams {
  type?: string;
  code: string;
  successfullyApplied: boolean;
  message: string;
  discountValue?: number | string;
  additionalOpts?: object;
}

interface TrackCodeRemovedParams {
  type?: string;
  code: string;
  discountValue?: number | string;
  additionalOpts?: object;
}

interface SubscriptionEventPayload {
  subscriptionUUID: string;
  paymentOption: PaymentOptionEnum;
  bundleSize: number;
  product: ProductPayload;
  url: string;
}

export enum SUBSCRIPTION_EVENT_TYPES {
  CANCEL_FLOW_STARTED = 'Cancel Flow Started',
  SKIP_KIT_FLOW_STARTED = 'Skip Kit Flow Started',
}

export enum CHECKOUT_EVENT_TYPES {
  CHECKOUT_CODE_APPLIED = 'Checkout Code Applied',
  CHECKOUT_CODE_REMOVED = 'Checkout Code Removed',
  ERROR_VIEWED = 'Error Viewed',
}

export enum PRODUCT_EVENT_TYPES {
  PRODUCT_VIEWED = 'Product Viewed',
  PRODUCT_CLICKED = 'Product Clicked',
  PRODUCT_ADDED = 'Product Added',
  PRODUCT_REMOVED = 'Product Removed',
  MEDIA_VIEWED = 'media_viewed',
}

export enum ITEM_EVENT_TYPES {
  ITEM_CLICKED = 'Item Clicked',
  BANNER_VIEWED = 'Banner Viewed',
  BANNER_CLICKED = 'Banner Clicked',
  BANNER_DISMISSED = 'Banner Dismissed',
}

export enum EXTENSION_UPSELL_EVENT_TYPES {
  EXTENSION_FLOW_STARTED = 'Extension Flow Started',
  EXTENSION_FLOW_STEP_VIEWED = 'Extension Flow Step Viewed',
  EXTENSION_FLOW_STEP_COMPLETED = 'Extension Flow Step Completed',
}

export const EXTENSION_UPSELL_EVENT_STEPS = {
  STARTED: { name: 'Extension Flow Started', stepNumber: '' },
  VIEWED: { name: 'Upsell Options', stepNumber: '2' },
  COMPLETED: { name: 'Extension Upsell', stepNumber: '' },
};

export enum CLICKED_ITEM_TYPES {
  LINK = 'Link',
  DISMISS = 'Dismiss',
  VIEW = 'View',
  BUTTON = 'Button',
  INPUT = 'Input',
  ANCHOR_LINK = 'Anchor Link',
  TAB = 'tab',
  CTA = 'CTA',
}

export enum PAGE_LOCATIONS {
  COURSE_PACKS_TILE = 'Course Packs tile',
  PLAY_KITS_TILE = 'Play Kits tile',
  SUBSCRIBER_SHOP_TILE = 'Subscriber Shop tile',
  ORDER_HISTORY_TILE = 'Order History tile',
  READING_SKILL_SET_TILE = 'Reading Skill Set tile',
  REFER_A_FRIEND_TILE = 'Refer A Friend tile',
  ACCOUNT_SETTINGS_TILE = 'Account Settings tile',
  MANAGE_SUBSCRIPTION_TILE = 'Manage Subscription tile',
  MANAGE_ERC_SUBSCRIPTION_TILE = 'Manage ERC Subscription',
  EARLY_READER_CLUB_SUBSCRIPTION_TILE = 'Early Reader Club Subscription tile',
  CUSTOMER_INFORMATION_CHECKOUT_PAGE = 'Customer Information Checkout Page',
  PAYMENT_CHECKOUT_PAGE = 'Payment Checkout Page',
  SITE_BANNER = 'Site Banner',
  PLAY_KITS_AGE_BASED = 'Explore The Play Kits by Age Module',
  NAVIGATION = 'Navigation',
  PLAY_KITS_PAGE = 'Play Kits Page',
  READING_SKILL_SET_PAGE = 'Reading Skill Set page',
  HOMEPAGE = 'Homepage',
  CHAT_WIDGET = 'Chat Widget',
  CUSTOMER_INFO = 'Customer Info',
  CHECKOUT = 'Checkout',
  EARLY_READER_CLUB_PAGE = 'Early Reader Club Page',
  PURCHASE_OPTIONS = 'Purchase Options',
  MODIFY_SHIP_DATE = 'Modify Ship Date',
  PLAY_KIT_MODIFY = 'Play Kit Modify Flow',
  PLAY_KIT_CANCEL = 'Play Kit Cancel Flow',
  EARLY_READER_CLUB_MODIFY = 'ERC Modify Flow',
  EARLY_READER_CLUB_CANCEL = 'ERC Cancel Flow',
  PLAY_KITS_PDP = 'Play Kits PDP',
}

export namespace PAGE_LOCATIONS {
  export enum SUBSCRIPTION_FLOW {
    CHILD_INFO = 'Child Info',
    STARTING_KIT = 'Starting Kit',
    PAYMENT_OPTIONS = 'Payment Options',
    ORDER_REVIEW = 'Order Review',
  }
}

export enum CTA_NAMES {
  VIEW = 'View',
  SKIP_KIT = 'Skip Kit',
  CANCEL_SUBSCRIPTION = 'Cancel Subscription',
  REMOVE_BOOK_BUNDLE = 'Remove Book Bundle',
  PREPAY_AND_SAVE_NOW = 'Prepay & Save Now',
  REACTIVATE_SUBSCRIPTION = 'Reactivate Subscription',
  SUBSCRIBE_NOW = 'Subscribe Now',
  ENABLE_ACCESSIBILITY = 'Enable Accessibility',
  EXIT_ACCESSIBILITY = 'Exit Accessibility Mode',
  ZERO_12_MONTHS = '0-12 Months',
  ONE_YEAR = '1-Year-Old',
  TWO_YEAR = '2-Year-Old',
  THREE_YEAR = '3-Year-Old',
  FOUR_YEAR = '4-Year-Old',
  CHILD_SELECTED = 'Child Selected',
  ADD_CHILD = 'Add Child',
  EDIT_CHILD = 'Edit Child',
  PICK_UP_WHERE_YOU_LEFT_OFF = 'Pick up where you left off',
  CHAT = 'Chat',
  EDIT = 'Edit',
  RETURN_TO_CART = 'Return to Cart',
  EDIT_SHIP_DATE = 'Edit Ship Date',
  EDIT_SHIPPING_ADDRESS = 'Edit Shipping Address',
  REPLACEMENT_PARTS = 'Replacement Parts',
  ADD_BOOKS_ADDON = 'Add Books Addon',
  MANAGE_PAYMENT_METHOD = 'Manage Payment Method',
  EXPLORE_THE_PLAY_KITS = 'Explore The Play Kits',
  EXTENSION_FLOW_STARTED = 'Extension Flow Started',
  GIFT_EXTENSION_FLOW_STARTED = 'Gift Extension Flow Started',
  BFCM_2024_BUNDLE_LEARN_MORE = 'Learn More',
  CONTINUE_TO_CHECKOUT = 'Continue to Checkout',
  SELECT_LINK = 'Select link',
  SELECT_GOOGLE_PAY = 'Select google_pay',
  SELECT_APPLE_PAY = 'Select apple_pay',
  SELECT_AMAZON_PAY = 'Select amazon_pay',
  SELECT_PAYPAL = 'Select paypal',
  CONTINUE_TO_SHIPPING_AND_PAYMENT = 'Continue to Shipping and Payment',
  SELECT_SHIPPING_OPTION = 'Select Shipping Option',
  PLACE_YOUR_ORDER = 'Place Your Order',
  SEE_IT_IN_ACTION = 'See it in action',
  LOOK_INSIDE = 'Look inside',
  USE_DIFFERENT_ADDRESS = 'use different address',
  SUBSCRIPTION_SELECTED = 'Subscription Selected',
  EXPLORE_THE_EARLY_READER_CLUB = 'Explore The Early Reader Club',
  SUBSCRIBE = 'Subscribe',
  BUY_NOW = 'Buy Now',
  ADD_BUTTON = 'Add',
  REMOVE_BUTTON = 'Remove',
  GET_STARTED = 'Get Started',
}

export enum PRODUCT_ADDED_COMPONENT_TYPES {
  PRIMARY_CTA = 'primary-cta',
  FULL_WIDTH_PRODUCT_CARD = 'full-width-product-card',
  PRODUCT_CARD = 'product-card',
  UPSELL_PDP = 'pdp-upsell',
  ROBUST_PRODUCT_CARD = 'robust-product-card',
  MODAL = 'modal',
}

export enum PRODUCT_ADDED_PAGE_TYPES {
  PRODUCT_DETAIL_PAGE = 'pdp',
  LANDING_PAGE = 'lp',
  SUB_SHOP = 'sub-shop',
}

export enum CUSTOMER_PORTAL_EVENT_TYPES {
  ACTION_SELECTED = 'Customer Portal Action Selected',
}

export enum CUSTOMER_PORTAL_ACTIONS {
  PAUSE = 'Pause',
  EDIT_SHIP_DATE = 'Edit Ship Date',
  SKIP = 'Skip',
  REMOVE_BOOK_BUNDLE = 'Remove Book Bundle',
  SWITCH_TO_ERC = 'Switch to ERC',
  ERC_STARTING_BOX = 'ERC Starting Box',
}

export enum SUBSCRIPTION_TYPES {
  PLAY_KIT = 'Play Kit',
  ERC = 'Early Reader Club',
}

export enum CUSTOMER_PORTAL_FLOWS {
  PLAY_KIT_MODIFY = 'Play Kit Modify Flow',
  PLAY_KIT_CANCEL = 'Play Kit Cancel Flow',
  EARLY_READER_CLUB_MODIFY = 'ERC Modify Flow',
  EARLY_READER_CLUB_CANCEL = 'ERC Cancel Flow',
  SUBSCRIPTION_CANCEL = 'Subscription Cancel Flow',
  CANCEL = 'Cancel',
  SWITCH_SUBSCRIPTIONS = 'Switch Subscriptions',
}

export enum SELECT_ITEM_TYPES {
  RADIO_BUTTON = 'radio_button',
  CHECKBOX = 'checkbox',
  DROPDOWN = 'dropdown',
}

export enum DRAWER_EVENT_TYPES {
  DRAWER_VIEWED = 'Drawer Viewed',
  DRAWER_CLOSED = 'Drawer Closed',
}

export type DrawerEventData = {
  content: string;
  contentType: string;
};

interface ProductEventPayload {
  url: string;
  productId: string;
  sku: string;
  category: string;
  name: string;
  ageRange?: string;
  ageUnits?: string;
  price: number | string;
  quantity: number;
  component?: string;
  pageType?: string;
}

interface ProductPayload {
  url: string | null | undefined;
  productId: string;
  sku: string;
  category: string;
  name: string | undefined;
  ageRangeMin: number | null | undefined;
  ageRangeMax: number | null | undefined;
  ageRange?: string;
  ageUnits: string;
  price: number | undefined;
}

interface FormErrorEventPayload {
  errorType: string;
  errors: string[];
  url: string;
}

interface ItemClickedEventPayload {
  url: string;
  type: CLICKED_ITEM_TYPES | ITEM_EVENT_TYPES;
  pageLocation: PAGE_LOCATIONS | PAGE_LOCATIONS.SUBSCRIPTION_FLOW | string;
  cta: CTA_NAMES | string;
}

interface ExtensionFlowStepEventPayload {
  stepName: string;
  url: string;
  bundleSize: string;
  platform: string;
  stepNumber?: string;
  subtotal?: string;
  discountValue?: string;
  products?: string[];
}

interface SubscriptionActionSelectedEventPayload {
  url: string;
  ui_type: SELECT_ITEM_TYPES;
  action: CUSTOMER_PORTAL_ACTIONS;
  flow: CUSTOMER_PORTAL_FLOWS;
  subscription_type: SUBSCRIPTION_TYPES;
}

interface CustomerPortalActionSelectedEventPayload {
  url: string;
  ui_type: SELECT_ITEM_TYPES;
  action: CUSTOMER_PORTAL_ACTIONS;
  flow: CUSTOMER_PORTAL_FLOWS;
}

const LE_REPO = 'lovevery-next';

function defaultTrackPayload() {
  return {
    le_repo: LE_REPO,
    lovevery_region: process.env.NEXT_PUBLIC_REGION,
  };
}

export function track(event: string, properties = {}) {
  window.rudderanalytics?.track(event, {
    ...defaultTrackPayload(),
    ...properties,
  });
}

export function pageEvent(category?: string) {
  window.rudderanalytics?.page(category, defaultTrackPayload());
}

export function trackProductEvent(
  eventName: PRODUCT_EVENT_TYPES,
  payload: ProductEventPayload,
) {
  track(eventName, payload);
}

export function trackExtensionFlowStepEvent(
  payload: ExtensionFlowStepEventPayload,
) {
  if (payload.stepName === EXTENSION_UPSELL_EVENT_STEPS.STARTED.name) {
    track(EXTENSION_UPSELL_EVENT_TYPES.EXTENSION_FLOW_STARTED, payload);
  } else if (payload.stepName === EXTENSION_UPSELL_EVENT_STEPS.COMPLETED.name) {
    track(EXTENSION_UPSELL_EVENT_TYPES.EXTENSION_FLOW_STEP_COMPLETED, payload);
  } else {
    track(EXTENSION_UPSELL_EVENT_TYPES.EXTENSION_FLOW_STEP_VIEWED, payload);
  }
}

export function trackItemClickedEvent(payload: ItemClickedEventPayload) {
  track(ITEM_EVENT_TYPES.ITEM_CLICKED, payload);
}

export function trackBannerViewedEvent(payload: ItemClickedEventPayload) {
  track(ITEM_EVENT_TYPES.BANNER_VIEWED, payload);
}

export function trackBannerClickedEvent(payload: ItemClickedEventPayload) {
  track(ITEM_EVENT_TYPES.BANNER_CLICKED, payload);
}

export function trackFormErrorEvent(payload: FormErrorEventPayload) {
  track(CHECKOUT_EVENT_TYPES.ERROR_VIEWED, payload);
}

export function trackBannerDismissedEvent(payload: ItemClickedEventPayload) {
  track(ITEM_EVENT_TYPES.BANNER_DISMISSED, payload);
}

export function trackCodeApplied(payload: TrackCodeAppliedParams) {
  track(CHECKOUT_EVENT_TYPES.CHECKOUT_CODE_APPLIED, payload);
}

export function trackCodeRemoved(payload: TrackCodeRemovedParams) {
  track(CHECKOUT_EVENT_TYPES.CHECKOUT_CODE_REMOVED, payload);
}

export function trackCancelSubscriptionEvent(
  payload: SubscriptionEventPayload,
) {
  track(SUBSCRIPTION_EVENT_TYPES.CANCEL_FLOW_STARTED, payload);
}

export function trackSkipKitFlowStartedEvent(
  payload: SubscriptionEventPayload,
) {
  track(SUBSCRIPTION_EVENT_TYPES.SKIP_KIT_FLOW_STARTED, payload);
}

export function trackSubscriptionActionSelected(
  payload: SubscriptionActionSelectedEventPayload,
) {
  track(CUSTOMER_PORTAL_EVENT_TYPES.ACTION_SELECTED, payload);
}

export function trackCustomerPortalActionSelectedEvent(
  payload: CustomerPortalActionSelectedEventPayload,
) {
  track(CUSTOMER_PORTAL_EVENT_TYPES.ACTION_SELECTED, payload);
}

export function buildPayloadUrl(fields: TypeLink_internalFields) {
  const internalLinkSlug = fields?.linkContent?.fields?.slug || '';

  const url = new URL(internalLinkSlug, window.location.href);
  return url.href;
}

export function cleanPayload(payload: Record<string, any>) {
  Object.keys(payload).forEach((key) => {
    if (
      payload[key] === null ||
      payload[key] === undefined ||
      payload[key] === ''
    ) {
      delete payload[key];
    }
  });

  return payload;
}

// Retry the identify call until the rudderanalytics object is available.
// TODO: Calling this userUuid - what is the difference between these two?
// user.attributes['custom:account_manager_id'] || user.attributes.loveveryId,
// Not awaiting this async because it could never clear
// Wait up to 5 seconds for the rudderanalytics object to be available
// IdentifyUserTraits has childBirthDate as a Date, but the API expects a string
//
export function queueIdentify(
  userUuid: string,
  traits: IdentifyUserTraits,
  retry = 0,
) {
  if (window.rudderanalytics) {
    const payload = cleanPayload({
      ...traits,
      childBirthDate: _extractDateYearAndMonth(traits.childBirthDate),
    });
    window.rudderanalytics.identify(userUuid, payload, {});
  } else if (retry < 20) {
    setTimeout(() => {
      queueIdentify(userUuid, traits, retry + 1);
    }, 250);
  }
}

export async function identifyCurrentUser(
  user: AuthState | null,
  userTraits: IdentifyUserTraits = {},
) {
  if (!user || !user.attributes) {
    noticeError('icua not present');
    return;
  }

  if (!user.attributes['custom:account_manager_id']) {
    noticeError('icuami not present');
    return;
  }

  queueIdentify(
    user.attributes['custom:account_manager_id'],
    cleanPayload({
      email: user.attributes.email,
      firstName: user.attributes.name,
      ...userTraits,
    }),
  );
}

export function _extractDateYearAndMonth(
  date: Date | undefined,
): string | null {
  if (typeof date === 'undefined') return null;

  return dayjs(date).format('YYYY-MM');
}

export const getAnonymousId = () => window.rudderanalytics?.getAnonymousId();
